/**
 * Where the swap happens — the one line the whole seam exists for.
 *
 * With no Supabase URL configured the app runs on fixtures and says so. With
 * one, every screen is reading a real database and not one of them changed.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Repo } from './repo.js';
import { fixtureRepo } from './fixtureRepo.js';

interface RepoContextValue { repo: Repo; usingFixtures: boolean }

const RepoContext = createContext<RepoContextValue>({
  repo: fixtureRepo, usingFixtures: true,
});

export function RepoProvider({ repo, children }: { repo?: Repo; children: ReactNode }) {
  // Derived from what was actually supplied, not from an environment variable.
  // A configured Supabase URL says nothing about which repo is wired up, and a
  // banner that says "live" over demo data is worse than no banner.
  const value = repo
    ? { repo, usingFixtures: false }
    : { repo: fixtureRepo, usingFixtures: true };
  return <RepoContext.Provider value={value}>{children}</RepoContext.Provider>;
}

export function useRepo(): Repo {
  return useContext(RepoContext).repo;
}

/** True while the app is running on fixtures, so the UI can admit it. */
export function useUsingFixtures(): boolean {
  return useContext(RepoContext).usingFixtures;
}

export type Async<T> =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; value: T };

/**
 * Load once per key.
 *
 * An error is a state the screen renders, never a silent empty list — an empty
 * jobs table and a failed jobs query look identical to a shop owner, and one of
 * them means their work is missing.
 */
export function useAsync<T>(load: () => Promise<T>, key: unknown[]): Async<T> {
  const [result, setResult] = useState<Async<T>>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    // NOT reset to 'loading' here. A revision bump after a write (save this
    // price, add a catalog row) refetches on the same key shape, and a caller
    // that renders "loading" as a full-screen replacement — JobShell does,
    // to hold four tabs and a whole job's unsaved draft state — would unmount
    // and remount the entire screen between one write and the next, losing
    // whatever had not yet reached the debounced autosave. The stale value
    // keeps rendering until the refetch actually resolves.
    load().then(
      (value) => { if (live) setResult({ state: 'ready', value }); },
      (err: unknown) => {
        if (live) {
          setResult({
            state: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      },
    );
    return () => { live = false; };
    // `load` is recreated every render; the key is what decides a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, key);

  return result;
}
